// src/components/Orders/Orders.js
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Container, Heading, ListContainer, FieldContainer, Input, Select, Table, TableRow, TableData, Error, LoadingContainer, LoadingSpinner, StatusBadge, StatusText, FlexContainer } from '../../styled/common';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const ResponsiveWrapper = styled.div`
  @media (max-width: 600px) {
    .hide-on-mobile {
      display: none !important;
    }
  }
`;

const Orders = ({ user }) => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [importantFilter, setImportantFilter] = useState('Yes');
  const [statusFilters, setStatusFilters] = useState({
    received: 'All',
    collection: 'All',
    utilisation: 'All',
  });
  const navigate = useNavigate();

  useEffect(() => {
    const fetchOrders = async () => {
      if (!user) {
        setError('User data is not available.');
        setOrders([]);
        setFilteredOrders([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const url =
          (user.role === 'tech' || user.role === 'warehouseAdmin')
            ? `/api/purchaseorders?state=${user.stateLocation}`
            : '/api/purchaseorders';

        const response = await axios.get(url);
        setOrders(response.data);
        setFilteredOrders(response.data);
      } catch (err) {
        console.error('Error fetching orders:', err);
        setError('Failed to load orders. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrders();
  }, [user]);

  useEffect(() => {
    filterOrders();
  }, [searchTerm, importantFilter, orders, statusFilters]);

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleFilterChange = (event) => {
    setImportantFilter(event.target.value);
  };

  const handleStatusFilterChange = (key, value) => {
    setStatusFilters((prev) => ({ ...prev, [key]: value }));
  };

  const filterOrders = () => {
    const filtered = orders.filter((order) => {
      const matchesSearch = order['PO Number'].toLowerCase().includes(searchTerm.toLowerCase());
      const matchesImportant = importantFilter === 'All' || (importantFilter === 'Yes' && order.importantFlag);
      const matchesReceived = statusFilters.received === 'All' || order.receivedStatus === statusFilters.received;
      const matchesCollection = statusFilters.collection === 'All' || order.collectionStatus === statusFilters.collection;
      const matchesUtilisation = statusFilters.utilisation === 'All' || order.utilisationStatus === statusFilters.utilisation;

      return matchesSearch && matchesImportant && matchesReceived && matchesCollection && matchesUtilisation;
    });

    setFilteredOrders(filtered);
  };

  const handleOrderClick = (orderNo) => {
    navigate(`/order/${orderNo}`);
  };

  if (!user) return <Error>User data is isLoading or not available.</Error>;
  if (isLoading) {
    return (
      <LoadingContainer>
        <LoadingSpinner />
      </LoadingContainer>
    );
  }
  if (error) return <Error>{error}</Error>;

  return (
    <ResponsiveWrapper>
      <Container>
        <ListContainer style={{ flex: '1 1 100%', maxWidth: '100%' }}>
          <Heading>Orders</Heading>
          <FlexContainer>
            <FlexContainer
              direction="row"
              justify="space-between"
              align="flex-start"
              gap="20px"
              style={{
                flexWrap: 'wrap',
                marginBottom: '20px',
                padding: '16px',
                backgroundColor: '#f9f9f9',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
              }}
            >
              <FieldContainer style={{ flex: '1', minWidth: '180px' }}>
                <label htmlFor="orderSearch">Search Orders:</label>
                <Input
                  id="orderSearch"
                  type="text"
                  placeholder="Search by Order Number"
                  value={searchTerm}
                  onChange={handleSearch}
                />
              </FieldContainer>

              <FieldContainer style={{ flex: '1', minWidth: '180px' }}>
                <label htmlFor="importantFilter">Filter by Important:</label>
                <Select id="importantFilter" value={importantFilter} onChange={handleFilterChange}>
                  <option value="Yes">Yes</option>
                  <option value="All">All</option>
                </Select>
              </FieldContainer>

              <FieldContainer style={{ flex: '1', minWidth: '180px' }}>
                <label htmlFor="receivedFilter">Received Status:</label>
                <Select id="receivedFilter" value={statusFilters.received} onChange={(e) => handleStatusFilterChange('received', e.target.value)}>
                  <option value="All">All</option>
                  <option value="Red">Red</option>
                  <option value="Yellow">Yellow</option>
                  <option value="Green">Green</option>
                </Select>
              </FieldContainer>

              <FieldContainer style={{ flex: '1', minWidth: '180px' }}>
                <label htmlFor="collectionFilter">Collection Status:</label>
                <Select id="collectionFilter" value={statusFilters.collection} onChange={(e) => handleStatusFilterChange('collection', e.target.value)}>
                  <option value="All">All</option>
                  <option value="Red">Red</option>
                  <option value="Yellow">Yellow</option>
                  <option value="Green">Green</option>
                </Select>
              </FieldContainer>

              <FieldContainer style={{ flex: '1', minWidth: '180px' }}>
                <label htmlFor="utilisationFilter">Utilisation Status:</label>
                <Select id="utilisationFilter" value={statusFilters.utilisation} onChange={(e) => handleStatusFilterChange('utilisation', e.target.value)}>
                  <option value="All">All</option>
                  <option value="Red">Red</option>
                  <option value="Yellow">Yellow</option>
                  <option value="Green">Green</option>
                </Select>
              </FieldContainer>
            </FlexContainer>

            <Table>
              <thead>
                <tr>
                  <th>Order Number</th>
                  <th className="hide-on-mobile">Ship To Party</th>
                  <th className="hide-on-mobile">Received Status</th>
                  <th className="hide-on-mobile">Collection Status</th>
                  <th className="hide-on-mobile">Utilisation Status</th>
                  <th className="hide-on-mobile">Important</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.length > 0 ? (
                  filteredOrders.map((order) => (
                    <TableRow key={order._id} onClick={() => handleOrderClick(order['PO Number'])}>
                      <TableData>{order['PO Number']}</TableData>
                      <TableData className="hide-on-mobile">{order['Ship To Party']}</TableData>
                      <TableData className="hide-on-mobile">
                        <StatusBadge status={order.receivedStatus.toLowerCase()} />
                      </TableData>
                      <TableData className="hide-on-mobile">
                        <StatusBadge status={order.collectionStatus.toLowerCase()} />
                      </TableData>
                      <TableData className="hide-on-mobile">
                        <StatusBadge status={order.utilisationStatus.toLowerCase()} />
                      </TableData>
                      <TableData className="hide-on-mobile">{order.importantFlag ? 'Yes' : ''}</TableData>
                    </TableRow>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" style={{ textAlign: 'center' }}>No orders available.</td>
                  </tr>
                )}
              </tbody>
            </Table>
          </FlexContainer>
        </ListContainer>
      </Container>
    </ResponsiveWrapper>
  );
};

export default Orders;
